import { useEffect, useRef } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { chartColors } from '../../config/trendsConfig';
import { chartTypes, getDefaultChartType } from '../../config/chartTypeConfig';
import {
  getLineChartData,
  getBarChartData,
  getAreaChartData,
  getDoughnutChartData,
  getChartOptions
} from './chartHelpers';
import ChartTypeSelector from './ChartTypeSelector';
import ChartLegend from './ChartLegend';

const TrendsChart = ({
  processedTrendData,
  showBreakdown,
  setShowBreakdown,
  isDark,
  selectedPeriod,
  chartType = 'line',
  setChartType,
  areaChartStacked = true,
  setAreaChartStacked
}) => {
  const average = processedTrendData.length > 0 ? Math.round(processedTrendData.reduce((sum, item) => sum + item.count, 0) / processedTrendData.length) : 0;
  const colors = { ...chartColors[isDark ? 'dark' : 'light'], isDark };

  // Chart reference for direct manipulation
  const chartRef = useRef(null);

  // Validate chart type when mode changes and switch to supported type if needed
  useEffect(() => {
    const currentChartConfig = chartTypes[chartType];
    const isSupported = showBreakdown ? currentChartConfig.supportsBreakdown : currentChartConfig.supportsSummary;

    if (!isSupported) {
      // Find first supported chart type for current mode
      const supportedType = Object.entries(chartTypes).find(([, config]) => {
        return showBreakdown ? config.supportsBreakdown : config.supportsSummary;
      });

      if (supportedType) {
        setChartType(supportedType[0]);
      } else {
        // Fallback to default if none found (shouldn't happen)
        setChartType(getDefaultChartType());
      }
    }
  }, [showBreakdown, chartType, setChartType]);

  // Get chart-specific data
  const getChartData = () => {
    let data;
    switch (chartType) {
      case 'line':
        data = getLineChartData(processedTrendData, showBreakdown, colors, average);
        break;
      case 'bar':
        data = getBarChartData(processedTrendData, showBreakdown, colors, average);
        break;
      case 'area':
        data = getAreaChartData(processedTrendData, showBreakdown, colors, average, areaChartStacked);
        break;
      case 'doughnut':
        data = getDoughnutChartData(processedTrendData, colors);
        break;
      default:
        data = getLineChartData(processedTrendData, showBreakdown, colors, average);
        break;
    }

    // No need for opacity manipulation - we directly manipulate chart data now

    return data;
  };

  const chartData = getChartData();

  const chartOptions = getChartOptions(chartType, isDark, selectedPeriod, areaChartStacked, colors);


  // Dynamic chart component selection
  const ChartComponent = () => {
    switch (chartType) {
      case 'line':
      case 'area':
        return <Line ref={chartRef} data={chartData} options={chartOptions} />;
      case 'bar':
        return <Bar ref={chartRef} data={chartData} options={chartOptions} />;
      case 'doughnut':
        return <Doughnut ref={chartRef} data={chartData} options={chartOptions} />;
      default:
        return <Line ref={chartRef} data={chartData} options={chartOptions} />;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8" data-intro="chart-area">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <ChartTypeSelector
            selectedType={chartType}
            onTypeChange={setChartType}
            showBreakdown={showBreakdown}
            isDark={isDark}
          />

          {chartType === 'area' && showBreakdown && (
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={areaChartStacked}
                onChange={(e) => setAreaChartStacked(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-2"
              />
              <span>Stacked</span>
            </label>
          )}
        </div>

        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 p-1" data-intro="chart-toggle">
          <button
            onClick={() => {
              setShowBreakdown(false);
              setAreaChartStacked(false);
            }}
            title="Summary view"
            className={`px-2 sm:px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center space-x-1 ${
              !showBreakdown
                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="hidden sm:inline">Summary</span>
          </button>
          <button
            onClick={() => {
              setShowBreakdown(true);
              setAreaChartStacked(true);
            }}
            title="Breakdown view"
            className={`px-2 sm:px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center space-x-1 ${
              showBreakdown
                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span className="hidden sm:inline">Breakdown</span>
          </button>
        </div>
      </div>

      <div style={{ height: '400px' }}>
        {processedTrendData.length > 0 ? (
          <ChartComponent />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400 font-medium">No data available</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try selecting a different date range or check your filters</p>
            </div>
          </div>
        )}
      </div>

      <ChartLegend
        chartRef={chartRef}
        showBreakdown={showBreakdown}
        chartType={chartType}
        areaChartStacked={areaChartStacked}
        colors={colors}
      />
    </div>
  );
};

export default TrendsChart;
